import type { NavigateFunction } from "react-router-dom";

import type { BrandBranchFormRouter } from "@/components/units/BrandFormBranches/BrandFormBranches";
import { PATHS } from "@/config/path";

import {
  BRANCH_WIZARD_RETURN_TO_KEY,
  BRAND_WIZARD_RETURN_TO_KEY,
  consumeWizardReturnTo,
  setWizardReturnTo,
} from "../wizardReturnTo";
import { BrandSnapshotRegenerator } from "./brandSnapshotRegenerator";
import type { DemoBrandStore } from "./DemoBrandStore";

const STEP_NAMES = ["details", "menu", "contacts", "branches"] as const;

export class BrandRouter implements BrandBranchFormRouter {
  private stepPaths: string[];
  private maxStep: number = 4;

  constructor(
    private navigate: NavigateFunction,
    private brandStore: DemoBrandStore,
    stepPaths: string[]
  ) {
    this.stepPaths = stepPaths;
  }

  setBrandId(brandId: string): void {
    this.stepPaths = [
      PATHS.ADMIN.BRAND_WIZARD_DETAILS(brandId),
      PATHS.ADMIN.BRAND_WIZARD_MENU(brandId),
      PATHS.ADMIN.BRAND_WIZARD_CONTACTS(brandId),
      PATHS.ADMIN.BRAND_WIZARD_BRANCHES(brandId),
    ];
  }

  private navigateToStep(step: number) {
    const stepIndex = step - 1;

    if (stepIndex < 0 || stepIndex >= this.stepPaths.length) {
      return;
    }

    const targetPath = this.stepPaths[stepIndex];
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete("returnTo");
    const queryString = searchParams.toString();
    const newPath = queryString ? `${targetPath}?${queryString}` : targetPath;

    this.navigate(newPath);
  }

  toNext() {
    const currentStep = this.getCurrentStep();
    if (currentStep < this.maxStep) {
      this.navigateToStep(currentStep + 1);
    }
  }

  toPrevious() {
    const currentStep = this.getCurrentStep();
    if (currentStep > 1) {
      this.navigateToStep(currentStep - 1);
    } else {
      this.close();
    }
  }

  private getCurrentStepFromPath(pathname: string): number {
    const pathWithoutQuery = pathname.split("?")[0];

    for (let i = 0; i < this.stepPaths.length; i++) {
      if (pathWithoutQuery === this.stepPaths[i]) {
        return i + 1;
      }
    }

    const pathParts = pathWithoutQuery.split("/").filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];
    const stepIndex = STEP_NAMES.findIndex(name => name === lastPart);
    if (stepIndex >= 0) {
      return stepIndex + 1;
    }

    return 1;
  }

  getCurrentStep(): number {
    return this.getCurrentStepFromPath(window.location.pathname);
  }

  private getGroupId(): string {
    const searchParams = new URLSearchParams(window.location.search);
    return (
      this.brandStore.details?.groupId || searchParams.get("groupId") || ""
    );
  }

  private getFallbackReturnTo(): string {
    const groupId = this.getGroupId();
    const brandId = this.brandStore.details?.id;

    if (groupId) {
      return PATHS.ADMIN.ACCOUNT_GROUP_DETAILS(groupId);
    }
    if (brandId) {
      return PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(brandId);
    }
    return PATHS.ADMIN.ACCOUNT;
  }

  close() {
    const returnTo =
      consumeWizardReturnTo(BRAND_WIZARD_RETURN_TO_KEY) ??
      this.getFallbackReturnTo();
    const groupId = this.getGroupId();

    this.navigate(returnTo, {
      state: groupId ? { groupId } : undefined,
    });
  }

  private getBrandWizardBranchesPath(): string {
    const brandId = this.brandStore.details?.id;
    const path = PATHS.ADMIN.BRAND_WIZARD_BRANCHES(brandId).split("?")[0];
    const params = new URLSearchParams(window.location.search);
    params.delete("returnTo");
    const groupId = this.getGroupId();
    if (groupId) {
      params.set("groupId", groupId);
    }
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }

  private navigateToBranchWizard(branchId?: string) {
    const snapshot = this.brandStore.getSnapshot();

    BrandSnapshotRegenerator.storeSnapshot(
      snapshot,
      window.location.pathname,
      this.navigate
    );

    setWizardReturnTo(
      BRANCH_WIZARD_RETURN_TO_KEY,
      this.getBrandWizardBranchesPath()
    );

    const branchPath = PATHS.ADMIN.BRANCH_WIZARD_DETAILS(
      branchId,
      this.brandStore.details?.id
    );

    this.navigate(branchPath, { state: snapshot });
  }

  toEditBranchDetails(branchId: string) {
    this.navigateToBranchWizard(branchId);
  }

  toNewBranchDetails() {
    this.navigateToBranchWizard(undefined);
  }
}
